import React from 'react';
import { Table2 } from 'lucide-react';

const TableSection = ({ section, className = '', ...props }) => {
  const { config, data } = section;

  const formatValue = (value, column) => {
    if (value === null || value === undefined) return '-';
    
    const format = column.format || {};
    
    // Handle date/datetime formatting
    if (column.type === 'date' || column.type === 'datetime') {
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return String(value);
        
        const formatStr = format.format || 'YYYY-MM-DD';
        return formatDate(date, formatStr);
      } catch (e) {
        return String(value);
      }
    }
    
    // Handle number formatting
    if (column.type === 'number' || column.type === 'decimal' || column.type === 'currency') {
      try {
        let num = parseFloat(value);
        if (isNaN(num)) return String(value);
        
        // Apply decimals
        if (format.decimals !== undefined) {
          num = num.toFixed(format.decimals);
        }
        
        // Apply thousands separator
        if (format.thousandsSeparator !== false) {
          num = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        
        // Apply prefix/suffix
        const prefix = format.prefix || format.symbol || '';
        const suffix = format.suffix || '';
        
        return `${prefix}${num}${suffix}`;
      } catch (e) {
        return String(value);
      }
    }
    
    return String(value);
  };

  const formatDate = (date, format) => {
    const pad = (n) => n < 10 ? '0' + n : n;
    
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthNamesFull = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    let result = format;
    result = result.replace('YYYY', year);
    result = result.replace('MM', pad(month));
    result = result.replace('DD', pad(day));
    result = result.replace('MMMM', monthNamesFull[month - 1]);
    result = result.replace('MMM', monthNames[month - 1]);
    result = result.replace('HH', pad(hours));
    result = result.replace('hh', pad(hours % 12 || 12));
    result = result.replace('mm', pad(minutes));
    result = result.replace('ss', pad(seconds));
    result = result.replace('A', hours >= 12 ? 'PM' : 'AM');
    
    return result;
  };

  if (!data || !data.rows || data.rows.length === 0) {
    return (
      <div className={`p-6 ${className}`} {...props}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {config.title}
        </h3>
        <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-400">
          <Table2 className="w-12 h-12 mb-2" />
          <p>No data available</p>
        </div>
      </div>
    );
  }

  const { columns, rows } = data;

  return (
    <div className={`p-6 ${className}`} {...props}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {config.title}
      </h3>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
              >
                {columns.map((column, colIndex) => {
                  const value = row[column.field];
                  const displayValue = formatValue(value, column);
                  
                  return (
                    <td
                      key={colIndex}
                      className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100"
                    >
                      {column.type === 'aggregation' ? (
                        <span className="font-semibold">{displayValue}</span>
                      ) : (
                        displayValue
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
        Total rows: {rows.length}
      </div>
    </div>
  );
};

export default TableSection;

